import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { DatePicker, Dropdown, Empty, Tooltip } from "antd";
import type { MenuProps } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  ArrowsAltOutlined,
  HistoryOutlined,
  NotificationOutlined,
  ReloadOutlined,
  ShrinkOutlined,
} from "@ant-design/icons";
import { CompassParagraph } from "../styledComponents";
import { CompassProgressBar } from "../CompassProgressBar";
import { CompassWhiteButton } from "../../buttons/styledComponents";
import { ElementContainer } from "../../containers/styledComponents";
import { LegendCircle } from "../../visuals/styledComponents";
import {
  GetCompanyNewsResponse,
  NewsPreparationProgress,
  NewsSearch,
  PublishedRange,
} from "../../../models/newsModels";
import { darkGreen, greyText, lightGreyText } from "../../../styles";
import { MESSAGES } from "../../../messages";
import { EvidenceDirection } from "../../../enums/News";
import { NewsDayBars } from "./NewsDayBars";
import { NewsTimeline } from "./NewsTimeline";
import {
  DIRECTION_COLORS,
  DIRECTION_LABELS,
  LEGEND_DIRECTIONS,
  NEWS_MAX_RANGE_DAYS,
  NEWS_RANGE_PRESET_DAYS,
  NEWS_WINDOW_DAYS,
} from "./constants";
import {
  coversNewsRange,
  formatNewsRange,
  formatSearchedAt,
  isTrailingNewsRange,
  totalsByDirection,
} from "./utils";

type NewsExpandControl = {
  expanded: boolean;
  onToggle: () => void;
};

type NewsRefreshControl = {
  refreshLabel: string;
  generateLabel: string;
  onRefresh: () => void;
  progress: NewsPreparationProgress | null;
};

type NewsRangeControl = {
  value: PublishedRange;
  onChange: (range: PublishedRange) => void;
};

type NewsTimelineCardProps = {
  news: GetCompanyNewsResponse;
  isLoading?: boolean;
  compact?: boolean;
  expandControl?: NewsExpandControl;
  refreshControl?: NewsRefreshControl;
  rangeControl?: NewsRangeControl;
  searches?: NewsSearch[];
};

const searchOutcome = (search: NewsSearch): string =>
  search.kept > 0
    ? MESSAGES.NEWS_FEED.NEW_COUNT(search.kept)
    : MESSAGES.NEWS_FEED.NOTHING_NEW_SHORT;

const searchMenuLabel = (search: NewsSearch): string =>
  [
    search.published_range
      ? formatNewsRange(search.published_range)
      : MESSAGES.NEWS_FEED.LATEST_NEWS,
    formatSearchedAt(search.searched_at),
    searchOutcome(search),
  ].join(" · ");

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${greyText};
  font-size: 15px;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 10px;
  min-width: 0;
`;

const LegendItem = styled.button<{ $dimmed: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  font-family: "Apercu";
  font-size: 11px;
  color: ${greyText};
  white-space: nowrap;
  cursor: pointer;
  opacity: ${({ $dimmed }): number => ($dimmed ? 0.4 : 1)};
  &:hover {
    color: ${darkGreen};
  }
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-right: -8px;
  padding-right: 8px;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  color: ${greyText};
  cursor: pointer;
  &:hover {
    color: ${darkGreen};
  }
  &:disabled {
    cursor: default;
    color: ${lightGreyText};
  }
`;

const progressText = ({ current, total }: NewsPreparationProgress): string =>
  total > 1
    ? `${MESSAGES.NEWS_FEED.PREPARING} · ${current}/${total}`
    : MESSAGES.NEWS_FEED.PREPARING;

const RANGE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] =
  NEWS_RANGE_PRESET_DAYS.map(({ label, days }) => ({
    label,
    value: [dayjs().startOf("day").subtract(days, "day"), dayjs().startOf("day")],
  }));

const isOutsideNewsRange = (
  current: Dayjs,
  { from }: { from?: Dayjs },
): boolean =>
  current.isAfter(dayjs(), "day") ||
  (from !== undefined &&
    Math.abs(current.diff(from, "day")) > NEWS_MAX_RANGE_DAYS);

const BAR_HEIGHT = 40;
const COMPACT_BAR_HEIGHT = 24;

export const NewsTimelineCard = ({
  news,
  isLoading = false,
  compact = false,
  expandControl,
  refreshControl,
  rangeControl,
  searches = [],
}: NewsTimelineCardProps): JSX.Element => {
  const totals = useMemo(
    () => totalsByDirection(news.daily_counts),
    [news.daily_counts],
  );
  const [activeDirection, setActiveDirection] =
    useState<EvidenceDirection | null>(null);
  const visibleItems = useMemo(
    () =>
      activeDirection === null
        ? news.items
        : news.items.filter(
            (item) => item.evidence_direction === activeDirection,
          ),
    [news.items, activeDirection],
  );
  const isEmpty = news.total === 0;
  const isRefreshing = Boolean(refreshControl?.progress);
  const rangeValue = useMemo<[Dayjs, Dayjs] | null>(
    () =>
      rangeControl
        ? [
            dayjs(rangeControl.value.published_from),
            dayjs(rangeControl.value.published_to),
          ]
        : null,
    [rangeControl],
  );
  const coveringSearch = rangeControl
    ? searches.find((search) => coversNewsRange(search, rangeControl.value))
    : undefined;
  const emptyMessage = coveringSearch
    ? MESSAGES.NEWS_FEED.SEARCHED_EMPTY(
        formatSearchedAt(coveringSearch.searched_at),
      )
    : rangeControl &&
        !isTrailingNewsRange(rangeControl.value, NEWS_WINDOW_DAYS)
      ? MESSAGES.NEWS_FEED.EMPTY_RANGE
      : MESSAGES.NEWS_FEED.EMPTY;
  const searchMenuItems: MenuProps["items"] = searches.map((search, index) => ({
    key: String(index),
    label: searchMenuLabel(search),
    disabled: !rangeControl || search.published_range === null,
  }));
  const handleSearchSelect: MenuProps["onClick"] = ({ key }) => {
    const range = searches[Number(key)]?.published_range;
    if (rangeControl && range) {
      rangeControl.onChange(range);
    }
  };

  const handleRangeChange = (range: [Dayjs | null, Dayjs | null] | null): void => {
    if (!rangeControl || !range || !range[0] || !range[1]) {
      return;
    }
    rangeControl.onChange({
      published_from: range[0].format("YYYY-MM-DD"),
      published_to: range[1].format("YYYY-MM-DD"),
    });
  };

  return (
    <Frame>
      <Header>
        <Title>
          <NotificationOutlined />
          <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
            {MESSAGES.NEWS_FEED.TITLE}
          </CompassParagraph>
        </Title>
        <HeaderActions>
          {searches.length > 0 && (
            <Dropdown
              menu={{ items: searchMenuItems, onClick: handleSearchSelect }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Tooltip title={MESSAGES.NEWS_FEED.RECENT_SEARCHES}>
                <IconButton
                  type="button"
                  aria-label={MESSAGES.NEWS_FEED.RECENT_SEARCHES}
                >
                  <HistoryOutlined />
                </IconButton>
              </Tooltip>
            </Dropdown>
          )}
          {refreshControl && !isLoading && !isEmpty && (
            <Tooltip title={refreshControl.refreshLabel}>
              <IconButton
                type="button"
                onClick={refreshControl.onRefresh}
                disabled={isRefreshing}
                aria-label={refreshControl.refreshLabel}
              >
                <ReloadOutlined />
              </IconButton>
            </Tooltip>
          )}
          {expandControl ? (
            <IconButton
              type="button"
              onClick={expandControl.onToggle}
              aria-label={
                expandControl.expanded
                  ? MESSAGES.NEWS_FEED.COLLAPSE
                  : MESSAGES.NEWS_FEED.EXPAND
              }
            >
              {expandControl.expanded ? (
                <ShrinkOutlined />
              ) : (
                <ArrowsAltOutlined />
              )}
            </IconButton>
          ) : rangeValue ? (
            <DatePicker.RangePicker
              size="small"
              value={rangeValue}
              onChange={handleRangeChange}
              presets={RANGE_PRESETS}
              disabledDate={isOutsideNewsRange}
              allowClear={false}
              format="D MMM YY"
              style={{ width: 200 }}
            />
          ) : (
            <CompassParagraph color={lightGreyText} fontSize={12} mr={0}>
              {MESSAGES.NEWS_FEED.WINDOW}
            </CompassParagraph>
          )}
        </HeaderActions>
      </Header>
      {refreshControl?.progress && (
        <div>
          <CompassParagraph color={greyText} fontSize={12} mb={4}>
            {progressText(refreshControl.progress)}
          </CompassParagraph>
          <CompassProgressBar
            value={refreshControl.progress.current}
            max={Math.max(refreshControl.progress.total, 1)}
            height={6}
          />
        </div>
      )}
      {isLoading ? null : isEmpty ? (
        <ElementContainer
          ai="center"
          jc="center"
          h="100%"
          w="100%"
          flexDirection="column"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <CompassParagraph color={greyText} fontSize={13} mr={0} ta="center">
                {emptyMessage}
              </CompassParagraph>
            }
          />
          {refreshControl && !isRefreshing && (
            <Tooltip title={refreshControl.generateLabel}>
              <CompassWhiteButton
                size="s"
                style={{ marginRight: 0 }}
                onClick={refreshControl.onRefresh}
              >
                {MESSAGES.NEWS_FEED.GENERATE_SHORT}
              </CompassWhiteButton>
            </Tooltip>
          )}
        </ElementContainer>
      ) : (
        <>
          <div>
            <Summary>
              <CompassParagraph
                color={darkGreen}
                fontSize={22}
                display="inline"
              >
                {news.total}
                <CompassParagraph
                  as="span"
                  color={greyText}
                  fontSize={13}
                  ml={6}
                  display="inline"
                >
                  {news.total === 1 ? "story" : "stories"}
                </CompassParagraph>
              </CompassParagraph>
              <Legend>
                <LegendItem
                  type="button"
                  $dimmed={activeDirection !== null}
                  aria-pressed={activeDirection === null}
                  onClick={(): void => setActiveDirection(null)}
                >
                  {MESSAGES.NEWS_FEED.ALL}
                </LegendItem>
                {LEGEND_DIRECTIONS.filter(
                  (direction) => totals[direction] > 0,
                ).map((direction) => (
                  <LegendItem
                    key={direction}
                    type="button"
                    $dimmed={
                      activeDirection !== null && activeDirection !== direction
                    }
                    aria-pressed={activeDirection === direction}
                    onClick={(): void =>
                      setActiveDirection((current) =>
                        current === direction ? null : direction,
                      )
                    }
                  >
                    <LegendCircle
                      color={DIRECTION_COLORS[direction]}
                      size={7}
                    />
                    {totals[direction]} {DIRECTION_LABELS[direction]}
                  </LegendItem>
                ))}
              </Legend>
            </Summary>
            <NewsDayBars
              days={news.daily_counts}
              height={compact ? COMPACT_BAR_HEIGHT : BAR_HEIGHT}
              barWidth="fill"
              showAxis={true}
            />
            {coveringSearch && (
              <CompassParagraph color={lightGreyText} fontSize={11} mt={4}>
                {MESSAGES.NEWS_FEED.SEARCHED(
                  formatSearchedAt(coveringSearch.searched_at),
                )}{" "}
                · {searchOutcome(coveringSearch)}
              </CompassParagraph>
            )}
          </div>
          <Scroll>
            <NewsTimeline
              items={visibleItems}
              compact={compact}
            />
          </Scroll>
        </>
      )}
    </Frame>
  );
};
